import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, of } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { AppointmentStatus, ObservationType } from '../../../core/enums';
import { HospitalEmployeeRole } from '../../../core/enums/hospital-employee-role.enum';

export interface Patient {
  id: string;
  first_name: string;
  last_name: string;
}

export interface AppointmentType {
  id: string;
  name: string;
}

export interface ServerAppointmentRequest {
  id: string;
  patient: Patient;
  hospital_employee_id: string;
  appointment_type: AppointmentType;
  hospital_id: string;
  scheduled_time: string;
  reason: string;
  appointment_status: AppointmentStatus;
  created_at: string;
  updated_at: string;
  duration?: number;
  observations?: ObservationType;
}

export interface ServerAppointmentResponse {
  items?: ServerAppointmentRequest[];
  data?: ServerAppointmentRequest[];
  limit?: number;
  offset?: number;
  total: number;
}

export interface ApiResponse {
  success: boolean;
  message?: string;
  data?: unknown;
}

export interface DepartmentServiceSelection {
  department_service_id: string;
  price: number;
}

export interface CompleteAppointmentPayload {
  department_services: DepartmentServiceSelection[];
  duration: number;
  observations: string;
}

export interface CompleteAppointmentResponse {
  success: boolean;
  message?: string;
  data?: unknown;
}

export interface RejectAppointmentPayload {
  new_scheduled_time: string;
  reason: string;
  doctor_note: string;
}

export interface RescheduleAppointmentPayload {
  new_scheduled_time: string;
}

export interface CountdownSettings {
  id: string;
  duration: number; // duration in minutes
}

export interface AppointmentRequest {
  id: string;
  bookingId: string;
  time: string;
  type: string;
  description: string;
  patient: string;
  status: AppointmentStatus;
  // Additional server data
  hospital_id: string;
  patient_id: string;
  hospital_employee_id: string;
  scheduled_time: string;
  appointment_type: AppointmentType;
  reason: string;
  created_at: string;
  updated_at: string;
  duration?: number;
  observations?: ObservationType;
}

@Injectable({
  providedIn: 'root',
})
export class AppointmentsService {
  private readonly apiService = inject(ApiService);
  private readonly authService = inject(AuthService);

  getAppointments(
    hospitalId: string,
    employeeId: string,
    filters?: {
      status?: string;
      date?: string;
      startDate?: string;
      endDate?: string;
      month?: string;
      year?: string;
      today?: boolean;
    },
    isCompletedToday?: boolean
  ): Observable<AppointmentRequest[]> {
    const user = this.authService.currentUser();
    const currentHospitalId = hospitalId || user?.hospital_id || '';
    const currentEmployeeId = employeeId || user?.id || '';
    const userRole = user?.role;

    const url = this.buildAppointmentsUrl(
      currentHospitalId,
      currentEmployeeId,
      filters,
      isCompletedToday,
      userRole
    );

    return this.apiService.authGet<ServerAppointmentResponse>(url).pipe(
      map((response: ServerAppointmentResponse) => {
        const appointments = response.items || response.data || [];
        if (appointments.length > 0) {
          return appointments.map((appointment: ServerAppointmentRequest) =>
            this.transformServerAppointmentToUI(appointment)
          );
        }
        return [];
      }),
      catchError(() => {
        return of([]);
      })
    );
  }

  private buildAppointmentsUrl(
    hospitalId: string,
    employeeId: string,
    filters?: {
      status?: string;
      date?: string;
      startDate?: string;
      endDate?: string;
      month?: string;
      year?: string;
      today?: boolean;
    },
    isCompletedToday?: boolean,
    userRole?: string
  ): string {
    let url = `secure/hospitals/${hospitalId}/appointments`;

    const queryParams: string[] = [];

    // Add employee_id as query parameter only if NOT a nurse
    if (employeeId && userRole !== HospitalEmployeeRole.NURSE) {
      queryParams.push(`employee_id=${employeeId}`);
    }

    if (filters) {
      const filterParams = this.buildQueryParams(filters, isCompletedToday);
      queryParams.push(...filterParams);
    } else if (isCompletedToday !== undefined) {
      queryParams.push(`is_completed_today=${isCompletedToday}`);
    }

    if (queryParams.length > 0) {
      url += `?${queryParams.join('&')}`;
    }

    return url;
  }

  private buildQueryParams(
    filters: {
      status?: string;
      date?: string;
      startDate?: string;
      endDate?: string;
      month?: string;
      year?: string;
      today?: boolean;
    },
    isCompletedToday?: boolean
  ): string[] {
    const queryParams: string[] = [];

    if (filters.status) {
      // Handle comma-separated status values as multiple query params
      const statuses = filters.status.split(',').map((s) => s.trim());
      for (const status of statuses) {
        queryParams.push(`status=${status}`);
      }
    }
    if (filters.date) {
      queryParams.push(`date=${filters.date}`);
    }
    if (filters.startDate) {
      queryParams.push(`start_date=${filters.startDate}`);
    }
    if (filters.endDate) {
      queryParams.push(`end_date=${filters.endDate}`);
    }
    if (filters.month) {
      queryParams.push(`month=${filters.month}`);
    }
    if (filters.year) {
      queryParams.push(`year=${filters.year}`);
    }
    if (filters.today !== undefined) {
      queryParams.push(`today=${filters.today}`);
    }
    if (isCompletedToday !== undefined) {
      queryParams.push(`is_completed_today=${isCompletedToday}`);
    }

    return queryParams;
  }

  approveAppointment(hospitalId: string, appointmentId: string): Observable<boolean> {
    return this.apiService
      .authPut(`secure/hospitals/${hospitalId}/appointments/${appointmentId}/approve`, {})
      .pipe(
        map((response: unknown) => {
          const apiResponse = response as ApiResponse;
          return apiResponse.success === true;
        }),
        catchError(() => of(false))
      );
  }

  declineAppointment(
    hospitalId: string,
    appointmentId: string,
    payload: RejectAppointmentPayload
  ): Observable<boolean> {
    return this.apiService
      .authPut(`secure/hospitals/${hospitalId}/appointments/${appointmentId}/reject`, payload)
      .pipe(
        map((response: unknown) => {
          const apiResponse = response as ApiResponse;
          return apiResponse.success === true;
        }),
        catchError(() => of(false))
      );
  }

  rescheduleAppointment(
    hospitalId: string,
    appointmentId: string,
    payload: RescheduleAppointmentPayload
  ): Observable<boolean> {
    return this.apiService
      .authPut(`secure/hospitals/${hospitalId}/appointments/${appointmentId}/reschedule`, payload)
      .pipe(
        map((response: unknown) => {
          const apiResponse = response as ApiResponse;
          return apiResponse.success === true;
        }),
        catchError(() => of(false))
      );
  }

  completeAppointment(
    hospitalId: string,
    appointmentId: string,
    payload: CompleteAppointmentPayload
  ): Observable<boolean> {
    return this.apiService
      .authPut(`secure/hospitals/${hospitalId}/appointments/${appointmentId}/complete`, payload)
      .pipe(
        map((response: unknown) => {
          const apiResponse = response as CompleteAppointmentResponse;
          return !!apiResponse.message;
        }),
        catchError(() => of(false))
      );
  }

  /**
   * Lock an appointment to prevent concurrent modifications
   * @param hospitalId - The ID of the hospital
   * @param appointmentId - The ID of the appointment to lock
   * @returns Observable with lock result
   */
  lockAppointment(
    hospitalId: string,
    appointmentId: string
  ): Observable<{ success: boolean; error?: string }> {
    return this.apiService
      .authPost(`secure/hospitals/${hospitalId}/appointments/${appointmentId}/lock`, {})
      .pipe(
        map(() => {
          return { success: true };
        }),
        catchError((error) => {
          if (error?.status === 409) {
            return of({
              success: false,
              error:
                error?.error?.message ||
                'Appointment is currently being processed by another user, please try again later',
            });
          }
          // Pour toutes les autres erreurs, retourner un message générique
          return of({
            success: false,
            error:
              error?.error?.message ||
              error?.message ||
              'Failed to lock appointment. Please try again.',
          });
        })
      );
  }

  /**
   * Unlock an appointment
   * @param hospitalId - The ID of the hospital
   * @param appointmentId - The ID of the appointment to unlock
   * @returns Observable with unlock result
   */
  unlockAppointment(
    hospitalId: string,
    appointmentId: string
  ): Observable<{ success: boolean; error?: string }> {
    return this.apiService
      .authDelete(`secure/hospitals/${hospitalId}/appointments/${appointmentId}/lock`)
      .pipe(
        map(() => {
          return { success: true };
        }),
        catchError(() => {
          return of({ success: false, error: 'Failed to unlock appointment' });
        })
      );
  }

  /**
   * Get countdown settings from the backend
   * @returns Observable with countdown settings (duration in minutes)
   */
  getCountdownSettings(): Observable<CountdownSettings | null> {
    return this.apiService.authGet<CountdownSettings>('secure/settings/countdowns').pipe(
      map((response: CountdownSettings) => {
        return response;
      }),
      catchError(() => {
        // Return null if the API call fails, component will use default value
        return of(null);
      })
    );
  }

  private transformServerAppointmentToUI(serverData: ServerAppointmentRequest): AppointmentRequest {
    const patientName = `${serverData.patient.first_name} ${serverData.patient.last_name}`;
    const formattedTime = this.formatScheduledTime(serverData.scheduled_time);
    const bookingId = `SGH${serverData.id.slice(-6).toUpperCase()}`;
    const description = `${serverData.appointment_type.name} - ${serverData.reason}`;

    return {
      id: serverData.id,
      bookingId: bookingId,
      time: formattedTime,
      type: serverData.appointment_type.name,
      description: description,
      patient: patientName,
      status: this.mapServerStatus(serverData.appointment_status),

      hospital_id: serverData.hospital_id,
      patient_id: serverData.patient.id,
      hospital_employee_id: serverData.hospital_employee_id,
      scheduled_time: serverData.scheduled_time,
      appointment_type: serverData.appointment_type,
      reason: serverData.reason,
      created_at: serverData.created_at,
      updated_at: serverData.updated_at,
      duration: serverData.duration,
      observations: serverData.observations,
    };
  }

  private formatScheduledTime(scheduledTime: string): string {
    try {
      const date = new Date(scheduledTime);
      const timeString = date.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      const endTime = new Date(date.getTime() + 30 * 60000); // Add 30 minutes
      const endTimeString = endTime.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
      return `${timeString} - ${endTimeString}`;
    } catch {
      return scheduledTime;
    }
  }

  private mapServerStatus(serverStatus: string): AppointmentStatus {
    switch (serverStatus) {
      case 'InReview':
        return AppointmentStatus.IN_REVIEW;
      case 'Scheduled':
        return AppointmentStatus.SCHEDULED;
      case 'Rescheduled':
        return AppointmentStatus.RESCHEDULED;
      case 'Cancelled':
        return AppointmentStatus.CANCELLED;
      case 'Completed':
        return AppointmentStatus.COMPLETED;
      default:
        return AppointmentStatus.IN_REVIEW;
    }
  }

  formatDisplayDate(scheduledTime: string): string {
    try {
      const date = new Date(scheduledTime);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return scheduledTime;
    }
  }

  getStatusBadge(status: string): { label: string; color: string } {
    switch (status) {
      case 'pending':
        return { label: 'Pending', color: 'bg-yellow-100 text-yellow-800' };
      case 'approved':
        return { label: 'Approved', color: 'bg-green-100 text-green-800' };
      case 'declined':
        return { label: 'Declined', color: 'bg-red-100 text-red-800' };
      default:
        return { label: 'Unknown', color: 'bg-gray-100 text-gray-800' };
    }
  }
}
